import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Truck, Calculator, Package, ArrowRight, CheckCircle2, Sparkles, Phone, HelpCircle } from 'lucide-react';

export const Servizi = () => {
  const services = [
    {
      icon: Truck,
      title: 'Ritiro Cavi Elettrici',
      description:
        'Servizio ritiro cavi per elettricisti, installatori ed imprese. Ritiro cavi cantiere e smaltimento cavi industriali in Canavese, Torino e provincia. Consegna diretta o ritiro in loco.',
      features: [
        'Ritiro in loco per quantità adeguate',
        'Consegna presso la nostra sede',
        'Smaltimento cavi fine cantiere',
        'Documentazione completa inclusa',
      ],
      color: 'from-[var(--copper-primary)] to-[var(--copper-dark)]',
    },
    {
      icon: Calculator,
      title: 'Quotazione Trasparente',
      description:
        'Acquistiamo i vostri cavi offrendo quotazioni chiare e competitive. Valutiamo i tuoi cavi elettrici da smaltire con prezzi basati su resa reale, non stime generiche.',
      features: [
        'Quotazioni immediate e trasparenti',
        'Prezzi di mercato competitivi',
        'Valutazione professionale',
        'Pagamento rapido e sicuro',
      ],
      color: 'from-[var(--verdigris)] to-[var(--verdigris-dark)]',
    },
    {
      icon: Package,
      title: 'Recupero Rame e Polimeri',
      description:
        'Riciclo cavi elettrici con recupero rame al 99% di purezza e riciclo polimeri di qualità. Materiali certificati pronti per essere reintrodotti nei cicli produttivi.',
      features: [
        'Granulato di rame 99% purezza',
        'Riciclo polimeri di qualità',
        'Economia circolare',
      ],
      color: 'from-[var(--copper-dark)] to-[var(--verdigris)]',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section Premium */}
      <section className="relative py-24 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-semibold">Soluzioni Complete</span>
            </div>
            <h1 className="text-6xl font-bold mb-6 animate-fade-in">I Nostri Servizi</h1>
            <p className="text-xl text-white/90 animate-fade-in leading-relaxed" style={{ animationDelay: '0.1s' }}>
              Soluzioni complete per il ritiro, lo smaltimento e il recupero di cavi elettrici in Canavese e provincia di Torino
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[var(--warm-gray)]">
        <div className="container">
          <div className="space-y-24">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    isEven ? '' : 'lg:grid-flow-dense'
                  }`}
                >
                  <div className={`${isEven ? '' : 'lg:col-start-2'}`}>
                    <div className="mb-8">
                      <div
                        className={`w-20 h-20 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}
                      >
                        <Icon className="h-10 w-10 text-white" />
                      </div>
                      <h2 className="text-4xl md:text-5xl font-bold mb-6">{service.title}</h2>
                      <p className="text-xl text-[var(--text-light)] leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                    <div className="space-y-4">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-[var(--copper-primary)] transition-all duration-300 hover:shadow-md">
                          <CheckCircle2 className="h-6 w-6 text-[var(--verdigris)] flex-shrink-0" />
                          <span className="text-lg text-[var(--text-dark)] font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={`${isEven ? '' : 'lg:col-start-1 lg:row-start-1'}`}>
                    <Card className="overflow-hidden border-2 hover:border-[var(--copper-primary)] transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                      <CardContent className="p-0">
                        <div className={`aspect-square bg-gradient-to-br ${service.color} flex items-center justify-center relative overflow-hidden`}>
                          <Icon className="h-48 w-48 text-white/20" />
                          <div className="absolute inset-0 bg-black/10"></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-5xl font-bold mb-6">Come Funziona</h2>
            <p className="text-xl text-[var(--text-light)] mb-16 leading-relaxed">
              Un processo semplice e trasparente in 4 step
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Contatto', desc: 'Contattaci per una valutazione' },
                { step: '02', title: 'Quotazione', desc: 'Ricevi una quotazione competitiva' },
                { step: '03', title: 'Ritiro', desc: 'Consegna in sede o ritiro su accordo' },
                { step: '04', title: 'Pagamento', desc: 'Ricevi il pagamento rapidamente' },
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[var(--copper-primary)] shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                    <div className="text-6xl font-extrabold text-[var(--copper-primary)] mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-[var(--text-light)] leading-relaxed">{item.desc}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className="h-8 w-8 text-[var(--copper-primary)]" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Two Boxes */}
      <section className="py-24 bg-gradient-to-b from-[var(--warm-gray)] to-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Box 1 - Contattaci */}
            <Card className="bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white border-0 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 md:p-10 flex flex-col h-full">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Contattaci Ora</h3>
                <p className="text-white/90 leading-relaxed mb-8 flex-grow">
                  Il nostro team è pronto ad aiutarti. Contattaci oggi stesso per discutere delle tue esigenze.
                </p>
                <Link to="/contatti">
                  <Button
                    size="lg"
                    className="w-full bg-white text-[var(--verdigris)] hover:bg-gray-100 font-semibold shadow-lg"
                  >
                    Contattaci Ora
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Box 2 - FAQ */}
            <Card className="bg-gradient-to-br from-[var(--copper-light)] to-[var(--copper-primary)] text-white border-0 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 md:p-10 flex flex-col h-full">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <HelpCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Domande Frequenti</h3>
                <p className="text-white/90 leading-relaxed mb-8 flex-grow">
                  Hai dubbi su quantitativi minimi, ritiri, pagamenti o tipologie di cavi? Consulta le FAQ per professionisti.
                </p>
                <Link to="/professionisti#domande-frequenti">
                  <Button
                    size="lg"
                    className="w-full bg-white text-[var(--copper-primary)] hover:bg-gray-100 font-semibold shadow-lg"
                  >
                    Vedi le FAQ
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};